import { computed, Injectable, signal } from '@angular/core';
import { KopConfigService } from '../core/kop-config.service';
import { JSBridgeService } from '../core/jsbridge.service';
import { KanaalService } from '../kanaal/kanaal.service';
import { KopModulesService } from '../modules/kop-modules.service';
import { cKC_Afdeling, cKC_Alarm, cKC_Analog, cKC_BijzPrg, cKC_LosDef, cKC_LosDefs, cKC_Servo, cKC_Uren } from '@wasm/KopWeb';
import { DigitalIn, LosDefType, ModuleAI, ModulePayload, ModuleUren } from './PropTypes';
import { wasmMapToRecord, wasmVectorToArray } from '../utils/wasmVector';
import { AfdModule, AfdModuleMeta } from '@etron/typings';

export type PanelType = "AI" | "LosDef" | "Servo" | "DrogWand" | "Koeling" | "Verwarming" | "AlarmText" | "Alarms" | "BijzPrgs" | "UrenTeksten" | "Uren";

const panelPlace: Record<PanelType, number> = {
  AI: 1,
  AlarmText: 2,
  Alarms: 3,
  Servo: 4,
  LosDef: 5,
  BijzPrgs: 6,
  Koeling: 7,
  DrogWand: 8,
  UrenTeksten: 9,
  Uren: 10,
  Verwarming: 11
};


@Injectable({
  providedIn: null
})
export class PropsServiceService {

  constructor(public kopConfigService: KopConfigService, public jsBridgeService: JSBridgeService,
    public kopModulesService: KopModulesService, public kanaalService: KanaalService) { }



  public isKlassiekPresentation = signal<boolean>(true);

  private afdInfos = computed(() => {
    let afd = this.kopConfigService.selectedAfd()?.afdeling();
    if (afd != undefined) {
      let info = this.kopConfigService.currentMeta().afdelingInfos.find(ai => ai.type == afd.type.value);
      return info;
    }

  });

  public hasPanelType(panel: PanelType) {
    let place = panelPlace[panel];
    let info = this.afdInfos();
    if (info != undefined) {
      let hasD = info.contain.get(place);
      return hasD == 1;
    }
    return false;
  }


  public getLosDefsByType(base: cKC_LosDefs | undefined, type: LosDefType) {
    let tmLosDefs = base;
    let ret: Array<cKC_LosDef> = [];
    if (tmLosDefs != undefined) {
      let sv = wasmVectorToArray(tmLosDefs).filter(ld => ld.waarde > 0);
      let selectedModule = this.kopModulesService.selectedAfdModule();
      if (selectedModule == undefined) {
        ret = sv;
      } else {
        ret = sv.filter(mld => selectedModule.meta.allLosDefsIndexes != undefined && selectedModule.meta.allLosDefsIndexes.indexOf(mld.index) >= 0);
      }
    }
    return ret.map(s => this.kopConfigService.findOrBuildLosDef(s.index, type)).sort((a, b) => {
      let aMeta = this.kopConfigService.currentMeta().losDefRaw.find(s => s.index == a.index);
      let bMeta = this.kopConfigService.currentMeta().losDefRaw.find(s => s.index == b.index);
      if (aMeta == undefined || bMeta == undefined) {
        return 0;
      }
      return aMeta.volgorde - bMeta.volgorde;
    });

  }

  public losDefs = computed(() => {
    return this.getLosDefsByType(this.kopConfigService.selectedAfd()?.afdeling().losDefs, 'losdef');
  });

  public drogWands = computed(() => {
    return this.getLosDefsByType(this.kopConfigService.selectedAfd()?.afdeling().droogWand, 'droogwand');
  });

  public koelings = computed(() => {
    return this.getLosDefsByType(this.kopConfigService.selectedAfd()?.afdeling().koeling, 'koeling');
  });

  public verwarmings = computed(() => {
    return this.getLosDefsByType(this.kopConfigService.selectedAfd()?.afdeling().verwarming, 'verwarming');
  });


  public ais = computed(() => {
    let tmAIs = this.kopConfigService.selectedAfd()?.afdeling().analogs;
    let ret: Array<cKC_Analog> = [];
    if (tmAIs != undefined) {
      let aiv = wasmVectorToArray(tmAIs);
      let selectedModule = this.kopModulesService.selectedAfdModule();
      if (selectedModule == undefined) {
        ret = aiv;
      } else {
        ret = aiv.filter(ai => selectedModule.meta.allAnalogIngagen != undefined && selectedModule.meta.allAnalogIngagen.indexOf(ai.index) >= 0);
      }
    }
    return ret.map(ai => this.kopConfigService.aiKCtoModule(ai.index, ai)).sort((a1, a2) => {
      return a1.kanaal - a2.kanaal;
    });
  });

  public alarms = computed(() => {
    let tmAlarmsW = this.kopConfigService.selectedAfd()?.afdeling().alarms;
    let ret: Array<cKC_Alarm> = [];
    if (tmAlarmsW != undefined) {
      let alarms = wasmVectorToArray(tmAlarmsW);
      let selectedModule = this.kopModulesService.selectedAfdModule();
      if (selectedModule == undefined) {
        ret = alarms;
      } else {
        ret = alarms.filter(a => selectedModule.meta.alarms != undefined && selectedModule.meta.alarms.indexOf(a.index) >= 0);
      }
      return ret;
    }
  });


  public servos = computed(() => {
    let tmServos = this.kopConfigService.selectedAfd()?.afdeling().servos;
    let ret: Array<cKC_Servo> = [];
    if (tmServos != undefined) {
      let sv = wasmVectorToArray(tmServos);
      let selectedModule = this.kopModulesService.selectedAfdModule();
      if (selectedModule == undefined) {
        ret = sv;
      } else {
        ret = sv.filter(servo => selectedModule.meta.allServos != undefined && selectedModule.meta.allServos.indexOf(servo.index) >= 0);
      }
    }
    return ret.map(s => this.kopConfigService.servoKCtoModule(s.index, s)).sort((s1, s2) => {
      let s1m = this.kopConfigService.currentMeta().servos.find(s => s.index == s1.index);
      let s2m = this.kopConfigService.currentMeta().servos.find(s => s.index == s2.index);
      return s1m!.prio - s2m!.prio;
    });
  });

  public bijzPrgs = computed(() => {
    let tmBPs = this.kopConfigService.selectedAfd()?.afdeling().bijzPrgs;
    let ret: Array<cKC_BijzPrg> = [];
    if (tmBPs != undefined) {
      let sv = wasmVectorToArray(tmBPs);
      let selectedModule = this.kopModulesService.selectedAfdModule();
      if (selectedModule == undefined) {
        ret = sv;
      } else {
        ret = sv.filter(bp => selectedModule.meta.allBijzPrgm != undefined && selectedModule.meta.allBijzPrgm.indexOf(bp.index) >= 0);
      }
    }
    return ret.map(bp => this.kopConfigService.bijzPrgmKCtoModule(bp.index, bp));
  });

  public uren = computed(() => {
    let tmUrenW = this.kopConfigService.selectedAfd()?.afdeling().urens;
    if (tmUrenW != undefined) {
      let ret = wasmMapToRecord(tmUrenW, e => e as cKC_Uren);
      return ret;
    }
  });

  public urenKeys = computed(() => {
    let u = this.uren();
    if (u == undefined) return [];
    return Object.keys(u).map(k => parseInt(k));
  });


  public urenModule = computed(() => {
    let ckUren = this.uren();
    if (ckUren != undefined) {

      let tmp = Object.entries(ckUren).map(([k, v]) => {
        let type = DigitalIn;
        let inv = this.kanaalService.getInvTab(v.kanaal, type);
        let ret: ModuleUren = {
          key: parseInt(k),
          blank: v.blank,
          kanaal: v.kanaal,
          no: v.no,
          type: v.type,
          inverted: inv
        }
        return [k, ret];
      });
      console.log(tmp);
      return Object.fromEntries(tmp);
    }
  });


  copySelectedModule(afdModule: AfdModule) {

    let selectedAfd = this.kopConfigService.selectedAfd();
    let payload: ModulePayload = {
      ais: [],
      servos: [],
      losdefs: [],
      drogWands: [],
      koelings: [],
      verwarmings: [],
      meta: afdModule
    };
    if (selectedAfd != undefined) {
      if (afdModule.meta.allAnalogIngagen != undefined) {
        let aims = this.ais();
        for (let ai of afdModule.meta.allAnalogIngagen) {
          let aim = aims.find(aim => aim.index == ai);
          if (aim != undefined) {
            payload.ais.push(aim);
          }
        }
      }
      if (afdModule.meta.allServos != undefined) {
        let servos = this.servos();
        for (let servo of afdModule.meta.allServos) {
          let s = servos.find(s => s.index == servo);
          if ((s != undefined) && (s.uitgang != 0)) {
            payload.servos.push(s);
          }
        }
      }
      if (afdModule.meta.allLosDefsIndexes != undefined) {
        let losDefs = this.losDefs();
        for (let losDef of afdModule.meta.allLosDefsIndexes) {
          let ld = losDefs.find(ld => ld.index == losDef);
          if (ld != undefined) {
            payload.losdefs.push(ld);
          }
        }
        let koelings = this.koelings();
        for (let koeling of afdModule.meta.allLosDefsIndexes) {
          let ld = koelings.find(ld => ld.index == koeling);
          if (ld != undefined) {
            payload.losdefs.push(ld);
          }
        }
        let verwarmings = this.verwarmings();
        for (let verwarming of afdModule.meta.allLosDefsIndexes) {
          let ld = verwarmings.find(ld => ld.index == verwarming);
          if (ld != undefined) {
            payload.losdefs.push(ld);
          }
        }
        let droogWands = this.drogWands();
        for (let droogWand of afdModule.meta.allLosDefsIndexes) {
          let ld = droogWands.find(ld => ld.index == droogWand);
          if (ld != undefined) {
            payload.losdefs.push(ld);
          }
        }
      }
    }
    let json = JSON.stringify(payload);

    navigator.clipboard.writeText(json);
    console.log(payload);

  }

}
